import { useEffect, useState } from 'react';
import TextWinds from '../../../components/common/TextWinds';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store/hook';
import { openAlert } from '../../../store/features/modalSlice';
import { getErrorMessages } from '../../../api/utils/util-func';
import { LoadingSpinner } from '../../../components/Loading/LoadingSpinner';
import {
  getAssignmentClassGroupList,
  getAssignmentStudentList,
} from '../../../api/utils/lms/assignment/api';
import ClassListContents from '../../../components/lms/dalant/ClassListContents';
import DalantByClassContents from '../../../components/lms/dalant/DalantByClassContents';
import LmsGuideButton from '../../../components/lms/LmsGuideButton';
import { LmsLinkItemKey } from '../../../constants/types/lms/type';

export const ALL_CLASS_ID = 'ALL' as const;

export type ClassOptionItem = {
  id: number | typeof ALL_CLASS_ID;
  name: string;
  studentCount: number;
};

/**
 * 개편 LMS (교사) - 달란트 관리 페이지
 */
const DalantManagement = () => {
  const { classId } = useParams();

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [classList, setClassList] = useState<ClassOptionItem[]>([]);

  useEffect(() => {
    if (!classId) return;

    const fetchAssignmentClassList = async () => {
      try {
        setIsLoading(true);

        const classes = await getAssignmentClassGroupList({
          classId,
          isActive: true, // 활성화된 반만 조회
        });

        const students = await getAssignmentStudentList({
          classId,
          classGroupId: [],
        });

        const studentCountByClassId = (students ?? []).reduce<
          Record<number, number>
        >((acc, student) => {
          const classGroupId = student.classInfos[0]?.classGroup?.id;

          if (classGroupId == null) return acc;

          acc[classGroupId] = (acc[classGroupId] ?? 0) + 1;

          return acc;
        }, {});

        const classList = [
          {
            id: ALL_CLASS_ID,
            name: '전체',
            studentCount: students?.length ?? 0,
          },
          ...(classes ?? []).map((classItem) => ({
            id: classItem.id,
            name: classItem.name,
            studentCount: studentCountByClassId[classItem.id] ?? 0,
          })),
        ];

        setClassList(classList);
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssignmentClassList();
  }, [classId, dispatch]);

  const [selectedClass, setSelectedClass] = useState<ClassOptionItem | null>(
    null,
  );

  const handleClassClick = (classItem: ClassOptionItem) => {
    setSelectedClass(classItem);
  };

  const handleBack = () => {
    setSelectedClass(null);
  };

  if (isLoading) {
    return <LoadingSpinner message="데이터 조회중" />;
  }

  return (
    <div className="flex w-full flex-col pt-6 xl:px-5">
      <header className="flex flex-col gap-3 px-5 xl:flex-row xl:items-center xl:justify-between xl:px-0">
        <div className="min-w-0">
          <TextWinds type="title_h2" xlType="title_h3" className="mb-[2px]">
            달란트 관리
          </TextWinds>

          <TextWinds type="content_body3">
            학생들의 달란트 현황을 확인하고, 달란트를 적립하거나 차감할 수
            있습니다.
          </TextWinds>
        </div>

        <LmsGuideButton itemKey={LmsLinkItemKey.DALANT_MANAGE} />
      </header>

      <div className="my-5 grid grid-cols-1 gap-4 px-3 xl:grid-cols-1 xl:px-0">
        <section className="rounded-xl bg-white p-4 shadow-card xl:p-6">
          {selectedClass ? (
            <>
              <DalantByClassContents
                selectedClass={selectedClass}
                handleBack={handleBack}
              />
            </>
          ) : (
            <ClassListContents
              classList={classList}
              handleClassClick={handleClassClick}
            />
          )}
        </section>
      </div>
    </div>
  );
};

export default DalantManagement;
